import { tx } from "../lib/i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Video, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API_BASE } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtFull, fmtTime } from "../lib/format";
import { Spinner } from "../components/site/Layout";
import { LiveRoom } from "../components/LiveRoom";

const RoomFrame = ({ title, sub, children }) => (
  <div className="osk-container py-6" data-testid="live-room-page">
    <div className="mb-4"><h1 className="text-2xl md:text-3xl">{title}</h1><p className="text-sm text-osk-text">{sub}</p></div>
    <div className="h-[70vh] min-h-[420px]">{children}</div>
  </div>
);

export function JoinPage() {
  const { token } = useParams();
  const { t, lang } = useI18n();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [access, setAccess] = useState(null);
  const load = () => api.get(`/join/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [token]); // eslint-disable-line
  const join = async () => {
    try { const { data } = await api.post(`/join/${token}/room`); setAccess(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const onEvent = (action, beacon) => beacon
    ? navigator.sendBeacon(`${API_BASE}/attendance/child`, new Blob([JSON.stringify({ token, action })], { type: "application/json" }))
    : api.post("/attendance/child", { token, action }).catch(() => {});
  if (err) return <div className="osk-container py-20 text-center" data-testid="join-error"><Lock className="mx-auto mb-3 h-10 w-10 text-osk-muted" /><p className="font-bold">{err}</p></div>;
  if (!info) return <Spinner />;
  const sub = `${info.child.name} · ${info.teacher?.display_name} · ${fmtFull(info.session.start, lang)} – ${fmtTime(info.session.end)}`;
  if (access) return <RoomFrame title={info.lesson.title} sub={sub}><LiveRoom access={access} name={info.child.name} onEvent={onEvent} /></RoomFrame>;
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-osk-light px-5 py-12" data-testid="join-page">
      <div className="osk-card w-full max-w-md space-y-4 p-8 text-center">
        <Video className="mx-auto h-12 w-12 text-osk-blue" />
        <h1 className="text-2xl">{info.lesson.title}</h1>
        <p className="text-sm text-osk-text">{sub}</p>
        <p className="text-xs text-osk-muted">{tx("Deze persoonlijke link werkt alleen voor deze les en voor")} {info.child.name}.</p>
        {info.can_join ? <button className="osk-btn-primary w-full" onClick={join} data-testid="join-lesson-btn">{t("join.btn")}</button> : (
          <>
            <button className="osk-btn-primary w-full" disabled data-testid="join-lesson-btn-disabled">{t("join.btn")}</button>
            <p className="flex items-center justify-center gap-2 text-sm text-osk-text" data-testid="join-opens-at"><Clock className="h-4 w-4" />
              {info.session.status !== "scheduled" ? tx("Deze les is niet meer actief.") : `${tx("Actief vanaf")} ${fmtFull(info.opens_at, lang)}`}</p>
          </>
        )}
      </div>
    </div>
  );
}

export function TeacherRoom() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.post(`/teacher/sessions/${sessionId}/room`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, [sessionId]);
  const onEvent = (action) => api.post("/attendance/teacher", { session_id: sessionId, action }).catch(() => {});
  if (err) return <div className="osk-container py-20 text-center" data-testid="teacher-room-error"><p className="font-bold">{err}</p><Link to="/docent" className="mt-4 inline-block text-osk-blue">{tx("Terug naar dashboard")}</Link></div>;
  if (!data) return <Spinner />;
  return (
    <RoomFrame title={data.lesson.title} sub={`${fmtFull(data.session.start)} – ${fmtTime(data.session.end)} · ${tx("Je bent de docent (host). Schermdelen staat aan, opnemen is uitgeschakeld.")}`}>
      <LiveRoom access={data} name={tx("Docent")} onEvent={onEvent} />
    </RoomFrame>
  );
}
